// Pomocné funkce a typy obrazovky 2. jakosti (sdílí stránka a buňka Shoptetu).

import { ApiError, apiFetch } from '@/lib/apiFetch';

export type KatalogInfo = { name: string; image: string; url: string };

export type Zalozena = {
  id: number;
  original_code: string;
  new_code: string;
  new_name: string | null;
  created_by: string;
  created_at: number;
  shoptet_cz_guid: string | null;
  shoptet_cz_sleva: number | null;
  /** Výjimky slevy po velikostech (kód varianty → %). */
  shoptet_cz_sleva_vel: Record<string, number>;
  /** Originál a 2. jakost v katalogu e-shopu CZ (null = v katalogu zatím není). */
  original_eshop: KatalogInfo | null;
  new_eshop: KatalogInfo | null;
  shoptet_cz_error: string | null;
};

/** Sleva v %: „30" / „12,5" → číslo v (0, 100); jinak null. */
export function parseSleva(v: string): number | null {
  const n = Number.parseFloat(v.replace(/\s+|%/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 && n < 100 ? n : null;
}

/** Stejné zaokrouhlení jako backend (`cenaPoSleve`): dolů na celé Kč, aspoň 1. */
export function cenaPoSleve(orig: number, sleva: number): number {
  return Math.max(1, Math.floor(orig * (1 - sleva / 100) + 1e-6));
}

export const fmtKc = (n: number): string => `${n.toLocaleString('cs-CZ')} Kč`;

/** Unix ms → „16. 10. 2026 15:20". */
export function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${hh}:${mm}`;
}

export function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

/** Založení 2. jakosti v Shoptetu CZ (sleva v % z ceny originálu). */
export async function zalozitShoptet(druhaId: number, slevaPct: number): Promise<string | null> {
  const res = await apiFetch<{ warning: string | null }>(
    `/api/druha-jakost/${druhaId}/shoptet-cz`,
    {
      method: 'POST',
      body: { sleva_pct: slevaPct },
    },
  );
  return res.warning;
}

type AkceRaw = { price?: string | null; fromDate?: string | null; toDate?: string | null };

/** „2026-09-30" → „30. 9." */
function fmtDen(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${Number(d)}. ${Number(m)}.`;
}

/**
 * Sloupec „Akční originálu": platná akce s cenou (a koncem, pokud má), prošlá
 * akce s poznámkou, že se nepoužije (pak se sleva počítá z běžné ceny).
 */
export function popisAkce(v: {
  orig_action: boolean;
  base_price: number | null;
  orig_action_raw: AkceRaw | null;
}): string {
  const raw = v.orig_action_raw;
  if (v.orig_action && v.base_price !== null) {
    return fmtKc(v.base_price) + (raw?.toDate ? ` (do ${fmtDen(raw.toDate)})` : '');
  }
  const cena = Number.parseFloat(raw?.price ?? '');
  if (!Number.isFinite(cena)) return '—';
  return `${fmtKc(cena)} (skončila${raw?.toDate ? ` ${fmtDen(raw.toDate)}` : ''})`;
}

// Typy obrazovky 2. jakosti (vyhledávání, návrh, náhled ze Shoptetu).

export type Produkt = {
  id: string;
  code: string;
  name: string;
  image: string;
  /** Hledaná 2. jakost — výběr otevře její originál a doplnění Shoptetu. */
  original_code?: string;
  /** Starší 2. jakost mimo Tools → nabídne napojení na originál. */
  legacy?: boolean;
};

export type Navrh = {
  original: Produkt;
  new_code: string;
  new_name: string;
  existing: Existujici[];
};

/** Už založená 2. jakost originálu (z `druha_jakost`). */
export type Existujici = { id: number; new_code: string; created_at: number; v_shoptetu: boolean };

export type ShoptetNahled = {
  category: string;
  flag: string;
  variants: {
    from: string;
    to: string;
    size: string;
    orig_price: number | null;
    base_price: number | null;
    orig_action: boolean;
    orig_action_raw: {
      price?: string | null;
      fromDate?: string | null;
      toDate?: string | null;
    } | null;
  }[];
  images: number;
};
